/**
 * Domain constants shared by the Worker API, the database layer and the web app.
 *
 * Money is represented everywhere as an integer number of minor units (cents).
 * Rates are represented as integer basis points (1 bp = 0.01%), so 8% = 800 bp.
 * Neither floats nor decimal strings are used for arithmetic anywhere in the
 * system; they only appear at the formatting boundary (see `money.ts`).
 */

export const STAFF_ROLES = ['ADMIN', 'MANAGER', 'WAITER', 'KITCHEN', 'BAR'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export const TABLE_STATUSES = ['AVAILABLE', 'OCCUPIED', 'WAITING_PAYMENT', 'RESERVED'] as const;
export type TableStatus = (typeof TABLE_STATUSES)[number];

export const ORDER_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'WAITING_PAYMENT',
  'PAID',
  'CANCELLED',
  'CLOSED',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_ITEM_STATUSES = [
  'DRAFT',
  'SENT',
  'PREPARING',
  'READY',
  'SERVED',
  'CANCELLED',
] as const;
export type OrderItemStatus = (typeof ORDER_ITEM_STATUSES)[number];

export const PRODUCTION_DESTINATIONS = ['KITCHEN', 'BAR', 'NONE'] as const;
export type ProductionDestination = (typeof PRODUCTION_DESTINATIONS)[number];

/** Destinations that actually generate a production ticket. */
export const TICKETED_DESTINATIONS = ['KITCHEN', 'BAR'] as const;
export type TicketedDestination = (typeof TICKETED_DESTINATIONS)[number];

export const TICKET_STATUSES = ['NEW', 'PREPARING', 'READY', 'COMPLETED'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TAX_TYPES = ['FOOD', 'ALCOHOL'] as const;
export type TaxType = (typeof TAX_TYPES)[number];

/** PERCENT stores basis points (1000 = 10%); FIXED stores an amount in cents. */
export const DISCOUNT_TYPES = ['PERCENT', 'FIXED'] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];

export const PAYMENT_METHODS = ['CASH', 'CREDIT_CARD', 'DEBIT_CARD', 'BANK_TRANSFER', 'OTHER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * How a guest found the restaurant, and whether they have been before. Both
 * are optional and feed only the guest-mix figures — never the bill. The
 * values are the exact strings the daily-report dashboard expects.
 */
export const GUEST_SOURCES = ['Facebook', 'Google', 'Connection', 'Walk-in', 'Hotline', '7Rooms'] as const;
export type GuestSource = (typeof GUEST_SOURCES)[number];

export const GUEST_STATUSES = ['New', 'Return'] as const;
export type GuestStatus = (typeof GUEST_STATUSES)[number];

/* ------------------------------ reservations ------------------------------ */

/**
 * A booking's life: taken, then either the party arrives and is seated (which
 * opens their table in the POS), or it ends as a no-show or a cancellation.
 * No-show and cancelled can be put back to booked, in case of a mistake.
 */
export const RESERVATION_STATUSES = ['BOOKED', 'SEATED', 'NO_SHOW', 'CANCELLED'] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

/** Where the booking came in. */
export const RESERVATION_CHANNELS = ['BISTROCHAT', 'PHONE', 'WALK_IN', 'OTHER'] as const;
export type ReservationChannel = (typeof RESERVATION_CHANNELS)[number];

export const RESERVATION_CHANNEL_LABELS: Record<ReservationChannel, string> = {
  BISTROCHAT: 'Bistrochat',
  PHONE: 'Phone',
  WALK_IN: 'Walk-in',
  OTHER: 'Other',
};

export const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  BOOKED: 'Booked',
  SEATED: 'Seated',
  NO_SHOW: 'No-show',
  CANCELLED: 'Cancelled',
};

/** How long a table is held for a booking unless the manager says otherwise. */
export const DEFAULT_RESERVATION_MINUTES = 120;

/** Courses 1..7, as required by the service flow. */
export const MIN_COURSE = 1;
export const MAX_COURSE = 7;
export const COURSES = [1, 2, 3, 4, 5, 6, 7] as const;
export type Course = (typeof COURSES)[number];

export const COURSE_LABELS: Record<number, string> = {
  1: 'Course 1',
  2: 'Course 2',
  3: 'Course 3',
  4: 'Course 4',
  5: 'Course 5',
  6: 'Course 6',
  7: 'Course 7',
};

/**
 * Default fiscal configuration. These are *defaults only* — the live values
 * come from `restaurant_settings`, and every order snapshots the values that
 * were in force when the order was taken.
 */
export const DEFAULT_FOOD_TAX_BP = 800; // 8%
export const DEFAULT_ALCOHOL_TAX_BP = 1000; // 10%
export const DEFAULT_SERVICE_CHARGE_BP = 500; // 5%
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_CURRENCY_SYMBOL = '$';
export const DEFAULT_CURRENCY_DECIMALS = true;

export const BASIS_POINT_DIVISOR = 10_000;

export const TAX_TYPE_LABELS: Record<TaxType, string> = {
  FOOD: 'Food & Non-Alcoholic',
  ALCOHOL: 'Alcohol',
};

export const DESTINATION_LABELS: Record<ProductionDestination, string> = {
  KITCHEN: 'Kitchen',
  BAR: 'Bar',
  NONE: 'No Ticket',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  BANK_TRANSFER: 'Bank Transfer',
  OTHER: 'Other',
};

export const ROLE_LABELS: Record<StaffRole, string> = {
  ADMIN: 'Administrator',
  MANAGER: 'Manager',
  WAITER: 'Cashier / Waiter',
  KITCHEN: 'Kitchen',
  BAR: 'Bar',
};

/* ------------------------------ bill printing ----------------------------- */

/**
 * The bill printer is wired to the cashier's machine, so a waiter on a phone
 * cannot reach it directly. Instead they enqueue a print job, which the print
 * station picks up and prints. A job is either the pre-payment bill carried to
 * the table or the settled receipt; both render from the same receipt payload.
 */
export const PRINT_JOB_KINDS = ['BILL', 'RECEIPT'] as const;
export type PrintJobKind = (typeof PRINT_JOB_KINDS)[number];

export const PRINT_JOB_STATUSES = ['QUEUED', 'PRINTING', 'DONE', 'FAILED', 'CANCELLED'] as const;
export type PrintJobStatus = (typeof PRINT_JOB_STATUSES)[number];

/** Statuses a job can still move out of — everything else is terminal. */
export const INVOICE_STATUSES = ['PENDING', 'ISSUED', 'FAILED'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const PENDING_PRINT_JOB_STATUSES = ['QUEUED', 'PRINTING'] as const;

export const PRINT_JOB_KIND_LABELS: Record<PrintJobKind, string> = {
  BILL: 'Bill',
  RECEIPT: 'Receipt',
};

export const PRINT_JOB_STATUS_LABELS: Record<PrintJobStatus, string> = {
  QUEUED: 'Waiting',
  PRINTING: 'Printing',
  DONE: 'Printed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

/**
 * A job left PRINTING for longer than this is assumed to have died with its
 * station — the browser was closed or the PC slept mid-print — and is returned
 * to the queue rather than being lost.
 */
export const PRINT_JOB_STALE_SECONDS = 90;

/** How many jobs a station takes in a single claim. */
export const PRINT_JOB_CLAIM_LIMIT = 5;

export const MAX_PRINT_COPIES = 5;
